import { Box, Button, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { THandleAction } from 'services/constants/tableStructureTypes';
import { COLORS } from 'services/constants/colors';
import { getPpwrCountryExclusionsRequest } from 'redux/reducers/ppwr/reducer';
import {
	getPpwrCountryExclusions,
	getPpwrCountryExclusionsIsLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrCountryExclusion } from 'redux/reducers/ppwr/types';
import { getAllCountries } from 'redux/reducers/settings/selectors';
import {
	PPWR_COUNTRY_EXCLUSION_ACTIONS,
	tableStructure,
	TPpwrCountryExclusionTableRow,
} from './tableStructure';
import { CountryExclusionDialog } from './CountryExclusionDialog';

const ROWS = Array.from(new Array(5));

type TDialogState = { open: false } | { open: true; exclusion: TPpwrCountryExclusion | null };

export const PpwrCountryExclusions: FC = () => {
	const dispatch = useDispatch();
	const countryExclusions = useSelector(getPpwrCountryExclusions);
	const countryExclusionsIsLoading = useSelector(getPpwrCountryExclusionsIsLoading);
	const allCountries = useSelector(getAllCountries);

	useEffect(() => {
		dispatch(getPpwrCountryExclusionsRequest());
	}, [dispatch]);

	const easTableItems = useMemo(() => {
		const rows: TPpwrCountryExclusionTableRow[] = (countryExclusions ?? []).map((exclusion) => ({
			id: exclusion.id,
			countryCode:
				allCountries?.find((country) => country.id === exclusion.countryId)?.isoAlpha2 ??
				String(exclusion.countryId),
			reason: exclusion.reason ?? '',
			reportsDoneBy: exclusion.reportsDoneBy ?? '',
			lucid: exclusion.lucid ?? '',
			validTill: exclusion.validTill,
			dashboardUpdatedAt: exclusion.dashboardUpdatedAt,
			dashboardUpdatedBy:
				exclusion.dashboardUpdatedBy?.fullName ?? exclusion.dashboardUpdatedBy?.email ?? '',
		}));
		return { data: rows, totalCount: rows.length };
	}, [countryExclusions, allCountries]);

	const excludedCountryIds = useMemo(
		() => (countryExclusions ?? []).map((exclusion) => exclusion.countryId),
		[countryExclusions],
	);

	const [dialog, setDialog] = useState<TDialogState>({ open: false });

	const handleAction: THandleAction = (id, type) => {
		if (type !== PPWR_COUNTRY_EXCLUSION_ACTIONS.EDIT) return;
		const exclusion = (countryExclusions ?? []).find((item) => item.id === id);
		if (exclusion) setDialog({ open: true, exclusion });
	};

	return (
		<>
			<EasTableWithoutFilter<TPpwrCountryExclusionTableRow>
				tableStructure={tableStructure}
				items={easTableItems}
				itemsIsLoading={countryExclusionsIsLoading}
				handleAction={handleAction}
				skeletonRows={ROWS}
				highlightedRow={null}
				paginationBlock={false}
			>
				<Box
					sx={{
						p: '2px 4px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography sx={{ fontSize: '1.2rem', color: COLORS.PRIMARY, fontWeight: 'bold' }}>
						ERP/PPWR Country Exclusion
					</Typography>
					<Button
						variant="contained"
						size="small"
						startIcon={<Add />}
						onClick={() => setDialog({ open: true, exclusion: null })}
					>
						Add New Country Exclusion
					</Button>
				</Box>
			</EasTableWithoutFilter>
			<CountryExclusionDialog
				open={dialog.open}
				exclusion={dialog.open ? dialog.exclusion : null}
				excludedCountryIds={excludedCountryIds}
				onClose={() => setDialog({ open: false })}
			/>
		</>
	);
};
